import React from "react"

import { Badge, Tooltip } from "@coval/ui"

/** Minimal slice of MetricRecord needed here; also satisfied by MetricRenderAdapter. */
interface MetricVersionStatusSource {
  getOutputVersionStatus: (metricOutput: {
    metric_version_number?: number | null
  }) => OutputVersionStatus
}

/** Version status of a single output, as returned by MetricRecord.getOutputVersionStatus. */
interface OutputVersionStatus {
  ranVersion: number
  currentVersion: number
  isStale: boolean
}

interface MetricVersionTagProps {
  /** The metric definition, used to resolve its current (live) version. */
  metric?: MetricVersionStatusSource | null
  /** The scored output whose version is being compared against the current one. */
  metricOutput?: { metric_version_number?: number | null } | null
  /**
   * Pre-computed result of metric.getOutputVersionStatus(metricOutput). When provided the
   * component skips its own getOutputVersionStatus call — for surfaces that have already computed
   * the status (e.g. to drive a conditional), or that only carry the status (e.g. table row data).
   */
  precomputedStatus?: OutputVersionStatus | null
  /**
   * "full" renders a labelled pill ("Ran with v2 — current is v4"); "compact" renders a small
   * version chip ("v2") for dense surfaces like the run-results grid. Both share one tooltip.
   */
  variant?: "full" | "compact"
  className?: string
}

/**
 * Warning indicator shown when a metric output was scored with a stale metric version.
 *
 * Renders nothing when the output is on the current version — only out-of-date results carry a
 * chip. Pre-versioning outputs are treated as v1 by getOutputVersionStatus, so they flag once
 * the metric moves past v1.
 */
export function MetricVersionTag({
  metric,
  metricOutput,
  precomputedStatus,
  variant = "full",
  className,
}: MetricVersionTagProps): React.ReactElement | null {
  const status =
    precomputedStatus !== undefined
      ? precomputedStatus
      : metric && metricOutput
        ? metric.getOutputVersionStatus(metricOutput)
        : null
  if (!status?.isStale) {
    return null
  }

  const tooltip = `This result was scored with v${status.ranVersion}. The metric is now on v${status.currentVersion}.`

  if (variant === "compact") {
    return (
      <Tooltip content={tooltip}>
        <Badge variant="warning" className={`shrink-0 ${className ?? ""}`}>
          v{status.ranVersion}
        </Badge>
      </Tooltip>
    )
  }

  return (
    <Tooltip content={tooltip}>
      <Badge variant="warning" className={className}>
        Ran with v{status.ranVersion} — current is v{status.currentVersion}
      </Badge>
    </Tooltip>
  )
}

interface MetricVersionSummaryTagProps {
  /** The metric definition, used to resolve per-output version statuses. */
  metric: MetricVersionStatusSource | null | undefined
  /** All scored outputs aggregated by the surface (e.g. one run-page metric card). */
  metricOutputs: Array<{ metric_version_number?: number | null }>
  className?: string
}

/**
 * Aggregate stale-version chip for surfaces that summarize many outputs of one metric, like the
 * top-level run-page metric cards. Renders nothing while all outputs are on the current version;
 * when any output ran on an older version it shows a warning chip with the transition
 * ("v4 → v5") so it reads consistently with the per-output ran-version chips on the same page.
 */
export function MetricVersionSummaryTag({
  metric,
  metricOutputs,
  className,
}: MetricVersionSummaryTagProps): React.ReactElement | null {
  if (!metric || metricOutputs.length === 0) {
    return null
  }

  const statuses = metricOutputs.map((output) =>
    metric.getOutputVersionStatus(output),
  )
  const currentVersion = statuses[0].currentVersion
  const staleStatuses = statuses.filter((status) => status.isStale)

  if (staleStatuses.length === 0) {
    return null
  }

  const ranVersion = Math.max(
    ...staleStatuses.map((status) => status.ranVersion),
  )

  return (
    <Tooltip
      content={`${staleStatuses.length} of ${statuses.length} results were scored with an older metric version. The metric is now on v${currentVersion}.`}
    >
      <Badge variant="warning" className={`shrink-0 ${className ?? ""}`}>
        v{ranVersion} → v{currentVersion}
      </Badge>
    </Tooltip>
  )
}
